import { ChangeLogTypes } from 'bundles/author-assignment-grading/constants/ChangeLog';
import type ChangeLogRecord from 'bundles/author-assignment-grading/models/ChangeLogRecord';

// Container to hold deduplicated change log records.
// Each group will be rendered as a single line in the table with expandable content.
export type ChangeLogGroup = {
  type: string;
  changeLogs: ChangeLogRecord[];
};

// Function decides if the two subsequent events are `itemGradingComplete` and `itemProgressComplete` and
// if they are related, so they can be merged.
const shouldMergeNextTwoGradingRecords = (changeLogs: ChangeLogRecord[], index: number) => {
  // return if there isn't next record
  if (index === changeLogs.length - 1) {
    return false;
  }

  const record = changeLogs[index];
  const nextRecord = changeLogs[index + 1];

  // merge only itemGradingComplete with itemProgressComplete records
  if (record.type !== ChangeLogTypes.itemGradingComplete) {
    return false;
  }

  if (nextRecord.type !== ChangeLogTypes.itemProgressComplete) {
    return false;
  }

  // do not merge if updates are not from the same grader
  if (record.staffInfo.id !== nextRecord.staffInfo.id) {
    return false;
  }

  // do not merge if records are more than 60 seconds apart
  if (Math.abs(record.date.timestamp - nextRecord.date.timestamp) > 60 * 1000) {
    return false;
  }

  return true;
};

// The function decides if we want to deduplicate events of the same type. At the moment we are deduplicating only
// `itemGradingComplete` events.
export const shouldAddRecordToLastGroup = (changeLogGroups: ChangeLogGroup[], record: ChangeLogRecord) => {
  if (changeLogGroups.length === 0) {
    return false;
  }

  // Only consecutive `itemGradingComplete` and `itemGradingAndProgressComplete` events are caused by auto-grading
  if (record.type !== ChangeLogTypes.itemGradingComplete) {
    return false;
  }

  const lastChangeLogGroup = changeLogGroups[changeLogGroups.length - 1];

  // do not merge if the last group has different type than the current event
  if (lastChangeLogGroup.type !== record.type) {
    return false;
  }

  const lastRecord = lastChangeLogGroup.changeLogs[lastChangeLogGroup.changeLogs.length - 1];

  // do not merge if updates are not from the same grader
  if (record.staffInfo.id !== lastRecord.staffInfo.id) {
    return false;
  }

  // do not add event to group if there is more than one-hour difference between the last event and the new event
  if (Math.abs(lastRecord.date.timestamp - record.date.timestamp) > 60 * 60 * 1000) {
    return false;
  }

  return true;
};

// This function will generate list of grading change log groups. There are two cases when we want to create
// a group with multiple change log records:
//
// 1. When comment is updated by auto-save - this will create a long list of `itemGradingComplete` events
// 2. When grade is updated by auto-save - this will create a long list of alternating `itemGradingComplete`
// and `itemProgressComplete` events
//
// In case of alternating events, we want to show only single event to user because from their perspective it's
// a single action. We will add the `itemGradingComplete` event to the group and omit the other one.
export const generateChangeLogGroups = (changeLogs: ChangeLogRecord[]): ChangeLogGroup[] => {
  // sort in chronological order
  changeLogs.sort((r1, r2) => r1.date.timestamp - r2.date.timestamp);

  const changeLogGroups: ChangeLogGroup[] = [];

  // Iterate over all change-log records, if we decide to group the current and the next record,
  // then we set the `skipNext` to true so the next one is skipped in the next iteration.
  let skipNext = false;

  changeLogs.forEach((record, index) => {
    if (skipNext) {
      skipNext = false;
      return;
    }

    // Find out if the next two events are related `itemGradingComplete`and `itemProgressComplete` events and
    // we should merge them to one.
    skipNext = shouldMergeNextTwoGradingRecords(changeLogs, index);

    // Find out if we want to deduplicate event
    const shouldAddToLastGroup = shouldAddRecordToLastGroup(changeLogGroups, record);

    if (shouldAddToLastGroup) {
      changeLogGroups[changeLogGroups.length - 1].changeLogs.push(record);
    } else {
      changeLogGroups.push({
        type: record.type,
        changeLogs: [record],
      });
    }
  });

  // sort in the reverse chronological order
  changeLogGroups.forEach((group) => group.changeLogs.reverse());
  changeLogGroups.reverse();

  return changeLogGroups;
};
